/**
 * Clay & Raw Material Weight Calculator — Sunstone Pottery Supply
 *
 * Moist Clay (product_type "Moist Clay"): quantity steps by 25, min 25
 * Raw materials (title starts with "Raw Material"): preset buttons 5, 10, 25, min 5
 * Both show per-lb price and total price.
 *
 * Also enforces increments in cart drawer and cart page.
 */
(function () {
  'use strict';

  // ── Shared helpers ────────────────────────────────────────────────
  // Only match the product page quantity input (name="quantity").
  // Cart inputs use name="updates[]" and must NOT be matched here — they are
  // handled separately in enforceCartQuantities().
  function getQtyInput() {
    return document.querySelector(
      'form[action*="/cart/add"] input[name="quantity"], ' +
      '.shopify-product-form input[name="quantity"], ' +
      'product-form-component input[name="quantity"], ' +
      '.product-details input[name="quantity"]'
    );
  }

  function getPerLbPrice() {
    var priceEl = document.querySelector('.product-details .price');
    if (!priceEl) return 0;
    var text = priceEl.textContent.replace(/[^0-9.]/g, '');
    return parseFloat(text) || 0;
  }

  function updateWeightDisplay() {
    var display = document.getElementById('clay-weight-total');
    if (!display) return;
    var qtyInput = getQtyInput();
    var quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    var perLb = getPerLbPrice();
    if (quantity > 0 && perLb > 0) {
      var total = (perLb * quantity).toFixed(2);
      display.innerHTML =
        '<span class="ss-price-total">$' + total + '</span>' +
        '<span class="ss-price-detail">' + quantity + ' lbs @ $' + perLb.toFixed(2) + '/lb</span>';
      display.style.display = 'block';
    } else {
      display.innerHTML = '';
      display.style.display = 'none';
    }
  }

  function createWeightDisplay() {
    var display = document.getElementById('clay-weight-total');
    if (display) return display;
    display = document.createElement('div');
    display.id = 'clay-weight-total';
    display.className = 'ss-clay-weight-display';

    // Preferred anchors (themed sections that render only when present)
    var target = document.querySelector('.product-details .variant-picker') ||
                 document.querySelector('.product-details .buy-buttons') ||
                 document.querySelector('.product-details .quantity');
    if (target) {
      target.parentNode.insertBefore(display, target);
      return display;
    }

    // Fallback: anchor to the quantity input — every product page with a buy
    // form has one, even single-variant products with no variant-picker.
    var qty = getQtyInput();
    if (qty) {
      var container = qty.closest('.quantity-selector, .quantity, [class*="quantity"], product-form-component, form[action*="/cart/add"]');
      if (container && container.parentNode) {
        container.parentNode.insertBefore(display, container);
        return display;
      }
    }

    // Last resort: append to product details so the price still renders
    // somewhere visible rather than silently disappearing.
    var details = document.querySelector('.product-details');
    if (details) details.appendChild(display);
    return display;
  }

  function listenForChanges() {
    document.addEventListener('input', function (e) {
      if (e.target.matches('input[name="quantity"], input[type="number"]')) updateWeightDisplay();
    });
    document.addEventListener('change', function (e) {
      if (e.target.matches('input[name="quantity"], select')) setTimeout(updateWeightDisplay, 50);
    });
    document.addEventListener('click', function (e) {
      if (e.target.closest('.quantity-selector button, .quantity button, .ss-qty-btn'))
        setTimeout(updateWeightDisplay, 100);
    });
    document.addEventListener('variant:change', updateWeightDisplay);
    document.addEventListener('VariantUpdateEvent', updateWeightDisplay);

    var form = document.querySelector('product-form-component, form[action*="/cart/add"]');
    if (form) {
      new MutationObserver(updateWeightDisplay).observe(form, {
        subtree: true, attributes: true,
        attributeFilter: ['value', 'aria-checked', 'class', 'data-selected-value']
      });
    }
  }

  // ── Detect product type ───────────────────────────────────────────
  function detectProductType() {
    var main = document.getElementById('MainContent');
    if (!main || !main.classList.contains('ss-per-lb-pricing')) return null;
    var productType = (main.getAttribute('data-product-type') || '').toLowerCase();
    if (productType === 'moist clay') return 'clay';
    return 'raw-material';
  }

  // ── Apply quantity constraints to an input ────────────────────────
  function enforceQty(input, min, step) {
    input.min = min;
    input.step = step;

    function snap() {
      var val = parseInt(input.value, 10) || 0;
      if (val < min) input.value = min;
      else if (val % step !== 0) input.value = Math.round(val / step) * step;
      updateWeightDisplay();
    }

    // Listen on multiple events
    input.addEventListener('change', snap);
    input.addEventListener('input', snap);
    input.addEventListener('blur', snap);

    // Override the value property so theme JS can't bypass us
    var descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    var enforced = { min: min, step: step };
    Object.defineProperty(input, 'value', {
      get: function () { return descriptor.get.call(this); },
      set: function (v) {
        var num = parseInt(v, 10) || 0;
        if (num < enforced.min) num = enforced.min;
        else if (num % enforced.step !== 0) num = Math.round(num / enforced.step) * enforced.step;
        descriptor.set.call(this, String(num));
        updateWeightDisplay();
      },
      configurable: true
    });

    // Watch for +/- button clicks on the parent container
    var container = input.closest('.quantity-selector, .quantity, [class*="quantity"]');
    if (container) {
      container.addEventListener('click', function () {
        setTimeout(snap, 50);
        setTimeout(snap, 150);
      }, true);
    }

  }

  // ── Clay body: 25-lb increments ───────────────────────────────────
  // Track currently wrapped input so we can re-bind if Shopify replaces it
  var wrappedClayInput = null;

  function wrapClayInput() {
    var input = getQtyInput();
    if (!input || input === wrappedClayInput) return false;
    wrappedClayInput = input;
    HTMLInputElement.prototype.__lookupSetter__('value').call(input, '25');
    enforceQty(input, 25, 25);
    updateWeightDisplay();
    return true;
  }

  function initClay() {
    if (!wrapClayInput()) return;
    createWeightDisplay();
    updateWeightDisplay();
    listenForChanges();
  }

  // ── Raw material: preset 5, 10, 25 buttons ────────────────────────
  function initRawMaterial() {
    var qtyInput = getQtyInput();
    if (!qtyInput) return;
    enforceQty(qtyInput, 5, 5);
    qtyInput.value = 5;

    var container = qtyInput.closest('.quantity-selector, .quantity, [class*="quantity"]');
    var buySection = container ? container.parentElement : null;
    if (!buySection) { createWeightDisplay(); updateWeightDisplay(); listenForChanges(); return; }

    var btnWrap = document.createElement('div');
    btnWrap.className = 'ss-qty-presets';
    btnWrap.innerHTML = '<span class="ss-qty-presets-label">Select weight:</span>';
    [5, 10, 25].forEach(function (lbs, idx) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ss-qty-btn' + (idx === 0 ? ' ss-qty-btn--active' : '');
      btn.textContent = lbs + ' lbs';
      btn.setAttribute('data-qty', lbs);
      btn.addEventListener('click', function () {
        qtyInput.value = lbs;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        btnWrap.querySelectorAll('.ss-qty-btn').forEach(function (b) {
          b.classList.toggle('ss-qty-btn--active', b === btn);
        });
        updateWeightDisplay();
      });
      btnWrap.appendChild(btn);
    });
    buySection.insertBefore(btnWrap, container);

    qtyInput.addEventListener('change', function () {
      var activeQty = parseInt(qtyInput.value, 10);
      btnWrap.querySelectorAll('.ss-qty-btn').forEach(function (b) {
        b.classList.toggle('ss-qty-btn--active', parseInt(b.getAttribute('data-qty')) === activeQty);
      });
    });

    createWeightDisplay();
    updateWeightDisplay();
    listenForChanges();
  }

  // ── Product page init (with retry for late-rendering themes) ──────
  function init() {
    var type = detectProductType();
    if (!type) return;

    var qtyInput = getQtyInput();
    if (qtyInput) {
      if (type === 'clay') initClay();
      else initRawMaterial();
    } else {
      // Theme may render quantity input after DOMContentLoaded — retry
      var attempts = 0;
      var interval = setInterval(function () {
        attempts++;
        if (getQtyInput()) {
          clearInterval(interval);
          if (type === 'clay') initClay();
          else initRawMaterial();
        } else if (attempts > 20) {
          clearInterval(interval);
        }
      }, 200);
    }
  }

  // ── Cart: enforce increments on all per-lb items ──────────────────
  // Works on both /cart page AND cart drawer (runs on any page)
  function enforceCartQuantities() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var rules = {}; // variant_id → { min, step }
        (cart.items || []).forEach(function (item) {
          var title = (item.title || '').toLowerCase();
          var ptype = (item.product_type || '').toLowerCase();
          if (ptype === 'moist clay') {
            rules[String(item.variant_id)] = { min: 25, step: 25 };
            rules[String(item.key)] = { min: 25, step: 25 };
          } else if (title.indexOf('raw material') === 0) {
            rules[String(item.variant_id)] = { min: 5, step: 5 };
            rules[String(item.key)] = { min: 5, step: 5 };
          }
        });

        if (Object.keys(rules).length === 0) return;

        // Find quantity inputs in cart page / cart drawer. Shopify wraps cart
        // inputs in <cart-quantity-selector-component data-variant-id="...">
        // so we climb up to find the variant ID.
        var qtyInputs = document.querySelectorAll('input[name="updates[]"], input[type="number"][data-cart-quantity]');
        qtyInputs.forEach(function (input) {
          var variantId =
            input.getAttribute('data-quantity-variant-id') ||
            (input.closest('[data-variant-id]') && input.closest('[data-variant-id]').getAttribute('data-variant-id')) ||
            '';
          var rule = rules[variantId];
          if (!rule) return;

          enforceQty(input, rule.min, rule.step);
          var val = parseInt(input.value, 10) || 0;
          if (val < rule.min) input.value = rule.min;
          else if (val % rule.step !== 0) input.value = Math.round(val / rule.step) * rule.step;
        });
      })
      .catch(function () {});
  }

  // ── Watch for cart drawer opening ─────────────────────────────────
  function watchCartDrawer() {
    // Re-run cart enforcement on clicks anywhere in cart UI (cart icon, +/-, etc.)
    document.addEventListener('click', function (e) {
      var cartEl = e.target.closest(
        'a[href="/cart"], [class*="cart-icon"], [class*="cart-toggle"], ' +
        'button[class*="cart"], cart-drawer, cart-quantity-selector-component, ' +
        '[class*="cart-item"], [class*="line-item"]'
      );
      if (cartEl) {
        setTimeout(enforceCartQuantities, 500);
        setTimeout(enforceCartQuantities, 1500);
      }
    });
  }

  // ── Bootstrap ─────────────────────────────────────────────────────
  function bootstrap() {
    init();
    enforceCartQuantities();
    watchCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
